import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import type { TicketResponses } from 'src/types/elements.type';
import { ApprovalStatus } from 'src/types/helpdesk/ticket.type';
import type { IResignationTicket, ResignationTicketAdd } from 'src/types/hr_ticket/resignationTicket.type';
import type { PagedList } from 'src/types/pagedList.type';
import type { IHrTicketParams } from 'src/types/params.type';
import { http } from '@martech-solution/utils/utils';
import type { RangeDateTypeHr } from 'src/types/date.type';

const controller = 'hr/resignation-tickets';

export const getResignationTickets = async (params: IHrTicketParams) => {
  return await http.get<PagedList<IResignationTicket>>(controller, {
    params: params
  });
};

export const getResignationTicketsApproval = async (params: IHrTicketParams) => {
  return await http.get<PagedList<IResignationTicket>>(`${controller}/waiting-approval`, {
    params: params
  });
};

export const getResignationTicket = async (id: number) => {
  return await http.get<IResignationTicket>(`${controller}/${id}`);
};

export const getResignationTicketProcessingCount = async (searchCondition?: string, approvalStatusId?: number) => {
  return await http.get(`${controller}/count-waiting-approvals`, {
    params: {
      searchCondition,
      approvalStatusId
    }
  });
};

export const addResignationTicket = async (resignationTicket: ResignationTicketAdd) => {
  return await http.post(controller, resignationTicket);
};

export const updateResignationTicket = async (id: number, resignationTicket: ResignationTicketAdd) => {
  return await http.put(`${controller}/${id}`, resignationTicket);
};

export const cancelResignationTicket = async (id: number) => {
  return await http.put(`${controller}/${id}/cancel`);
};

export const approvalResignationTickets = async (
  ticketResponses: TicketResponses[],
  approvalStatus: ApprovalStatus,
  note: string,
  replacerId?: number
) =>
  await http.put(`${controller}/approvals`, {
    ticketResponses: ticketResponses,
    approvalStatus,
    note: note ? note : '',
    replacerId: replacerId
  });

export const cancelApprovalResignationTicket = async (id: string, step: number) =>
  await http.put(`${controller}/${id}/cancel-approval`, { step });

export const hrConfirmResignationTicket = async (
  id: number | undefined,
  hrConfirmedType: ConfirmTypeEn,
  hrConfirmedDate: Date,
  hrConfirmedNote?: string,
  resignDate?: Date
) => {
  return await http.put(`${controller}/hr-confirmed/${id}`, {
    hrConfirmedType,
    hrConfirmedDate,
    hrConfirmedNote,
    resignDate
  });
};

export const getMyResignationTicketCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-my-tickets`, {
    params: params
  });
};

export const getResignationTicketApprovalCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-handle-tickets`, {
    params: params
  });
};

export const getResignationTicketWaitingProcessingCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-ticket-waiting-processing`, {
    params: params
  });
};

export const getResignationTicketHrConfirmCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-hr-confirm-tickets`, {
    params: params
  });
};

// HR
export const getResignationTicketsHrConfirm = async (
  fromDate: Date,
  toDate: Date,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  departmentId?: number | undefined,
  searchCondition?: string | undefined,
  confirmType?: ConfirmTypeEn | undefined
) => {
  const newFromDate = fromDate.toISOString();
  const newToDate = toDate.toISOString();
  return await http.get<PagedList<IResignationTicket>>(`${controller}/hr-confirm/${newFromDate}/${newToDate}`, {
    params: {
      searchCondition,
      departmentId,
      page,
      pageSize,
      orderBy,
      confirmType
    }
  });
};

// print
export const printResignationTicket = async (id: number) =>
  await http.get(`${controller}/print`, {
    params: {
      id: id
    },
    responseType: 'blob'
  });

export const exportResignationTickets = async (
  fromDate?: Date,
  toDate?: Date,
  departmentId?: number,
  searchCondition?: string
) => {
  return await http.get<IResignationTicket[]>(`${controller}/export`, {
    params: {
      fromDate,
      toDate,
      departmentId,
      searchCondition
    }
  });
};

export const getResignationTicketsReport = async (
  fromDate?: Date,
  toDate?: Date,
  departmentId?: number | undefined,
  searchCondition?: string | undefined,
  confirmType?: ConfirmTypeEn | undefined
) => {
  return await http.get<IResignationTicket[]>(`${controller}/hr-report`, {
    params: {
      fromDate,
      toDate,
      departmentId,
      searchCondition,
      confirmType
    }
  });
};
